import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, CheckCircle, Warning, Eye, Cursor, DownloadSimple } from "@phosphor-icons/react";
import api from "../lib/api";
import { downloadFromBackend } from "../lib/download";
import { formatTorontoTimestamp } from "../lib/timeDisplay";
// P4-N5B Slice C3 — canonical Network/Country adapter (C1 foundation).
// Consumed ONLY by the transaction-report flavour of this modal
// (`kind === "transaction_report"`). The price-list flavour is FROZEN
// (N5C) and its Network/Country/country-picker surface is not touched.
import {
  COUNTRY_LABELS,
  TRANSACTION_CAPABILITY,
  getVisibleCountries,
  toWireParams,
} from "../lib/networkCountryCatalog";

// "All networks" branch — union of every provider's transaction-country
// capability. Consumes the C1 capability matrix directly; does NOT
// duplicate the per-network capability rules inside this file.
const QUICK_EMAIL_TX_ALL_NETWORKS_COUNTRIES = (() => {
  const union = Array.from(
    new Set(Object.values(TRANSACTION_CAPABILITY).flat())
  );
  return union.length > 1 ? ["", ...union] : union;
})();

// Country options visible in the Transactions flavour of this modal for
// a given selected Network. Delegates to C1's `getVisibleCountries` for
// specific networks; falls back to the "All networks" union above when
// the operator has NOT selected a network.
function _quickEmailTxCountriesFor(network) {
  if (!network) return QUICK_EMAIL_TX_ALL_NETWORKS_COUNTRIES;
  return getVisibleCountries("transactions", network);
}

/**
 * One-click "send a fresh report" modal launched from the customer header.
 *
 * Two flavours selected by `kind`:
 *   "price_list"          — sends today's price list by default.
 *   "transaction_report"  — sends yesterday's transaction report by default.
 *
 * Renders the date pickers, subject/body, recipient + CC + send history all
 * in one modal so the operator can fire the email in 2 clicks.
 */
export default function QuickEmailReportModal({ kind, customer, customerId, onClose }) {
  // `today` / `tomorrow` / `yesterday` are intentionally calculated in
  // **America/Toronto** (EST/EDT) — not browser-local and not UTC.
  // Without this, an operator hitting "Send price report" at 9pm EST
  // on Jun 3 would see `Jun 4` (UTC has already rolled over), or — if
  // the operator is travelling — they'd see whatever date their
  // laptop's timezone happens to be on. Pricing is anchored on
  // Toronto-day boundaries throughout the rest of the app (see
  // `formatTorontoTimestamp`), so the price-report defaults must
  // follow the same rule.
  //
  // Price-list emails are FORWARD-looking — sent today for prices
  // that take effect TOMORROW — so the price flavour defaults to
  // tomorrow EST. Transaction reports are BACKWARD-looking (cover
  // yesterday's fueling activity), so they default to yesterday EST.
  const _torontoYmd = (offsetDays = 0) => {
    const d = new Date(Date.now() + offsetDays * 86400000);
    // `en-CA` returns YYYY-MM-DD which the <input type="date"> control wants.
    return d.toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
  };
  const tomorrow = _torontoYmd(1);
  const yesterday = _torontoYmd(-1);
  const isPrice = kind === "price_list";

  const [dateFrom, setDateFrom] = useState(isPrice ? tomorrow : yesterday);
  const [dateTo, setDateTo] = useState(isPrice ? tomorrow : yesterday);
  // Network scope for transaction-report flavour. P4-N5B Slice C3
  // canonicalised this to the C1 shared catalog: only canonical
  // `network ∈ { "", "esso", "pipeline", "petro_pass" }` is stored.
  // The country lives in its OWN state (line 62) and combines with
  // the network through C1's `toWireParams` before hitting the wire.
  // The compound legacy values (`petro_pass_ca`, `petro_pass_us`,
  // `ta_petro`) are gone — the `esso → {esso, ta_petro}` widening
  // is the backend's authority (12 call sites incl. `/admin/units`).
  const [network, setNetwork] = useState("");
  // Country scope. Always geographic (CA / US / "" for All). C3 widened
  // the visibility gate from esso-only to every non-Pipeline lane —
  // Pipeline is single-capability (CA-only) and locks the selector.
  const [country, setCountry] = useState("");
  // Per-Unit / Per-Card scope (June 2026 operator spec).
  //   • `cardOptions` lists every fuel card on this customer (filtered by
  //     the network dropdown when set) so the operator can pick one to
  //     download a single-unit sheet, OR
  //   • The "Download per-unit (zip)" button bypasses the dropdown and
  //     bundles every active card in the window as separate CSV+PDF
  //     files inside a single zip.
  // `cardId === ""` keeps the modal in "all units / all cards" mode
  // (the default), preserving existing behaviour for operators who
  // don't need per-unit reports.
  const [cardOptions, setCardOptions] = useState([]);
  const [cardId, setCardId] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  // CC default is UNCHECKED (per operator request). Operator manually
  // checks the box when they want a copy in hello@getfuelcard.com.
  const [cc, setCc] = useState(false);

  const recipient = customer.billing_email || customer.email || "";
  const noEmail = !recipient;
  // P4-N5C UX Correction R3 (2026-09-19): the price-list flavour of this
  // Quick Email modal is the operator-canonical "Email Price Report"
  // workflow. Its heading and its Download CSV / PDF chrome are aligned
  // with the email-delivery-only contract — the interactive View Price
  // Report workflow lives in `ReportModal(kind="price")` and is
  // untouched. Transaction-report flavour keeps its original chrome.
  const heading = isPrice ? "Email Price Report" : "Transactions";

  // P4-N5B Slice C3 — canonical wire builder. Delegates to C1's
  // `toWireParams` which emits ONLY canonical geography:
  //   network ∈ { "esso", "pipeline", "petro_pass" }
  //   country ∈ { "CA", "US", "" }
  // Never emits `ta` / `ta_petro` / compound. Backend widening
  // (`/admin/units` line 135, `_build_transactions_report`, +10 others)
  // owns the `esso → {esso, ta_petro}` union. `null` fallback preserves
  // the original send / download / zip payload shape which expected
  // `wireNetworkCountry.network` / `.country` to be null when absent.
  const wireNetworkCountry = React.useMemo(() => {
    const wire = toWireParams({ network, country });
    return {
      network: wire.network || null,
      country: wire.country || null,
    };
  }, [network, country]);

  // Keep the subject/body in sync with the date inputs but let the operator
  // override mid-flight without us clobbering their typing.
  const range_txt = dateFrom === dateTo ? dateFrom : `${dateFrom} → ${dateTo}`;
  const computedSubject = isPrice
    ? priceListDefaultSubject(customer.company_name)
    : `Transaction List - ${customer.company_name} - ${range_txt}`;
  const computedBody = isPrice
    ? priceListDefaultBody(customer.company_name)
    : `Hello,\n\nPlease find attached the transaction report for ${customer.company_name}, ` +
      `covering ${range_txt}.\n\nThe PDF lists every fueling — date, card, unit, station, qty, ` +
      "CPL and amount.\n\nIf anything looks off, just reply to this email.\n\n— Get Fuel Card";

  const [subject, setSubject] = useState(computedSubject);
  const [body, setBody] = useState(computedBody);
  const [touched, setTouched] = useState(false);
  // Track download-in-progress so we can disable the matching button and
  // surface a spinner. `null` means no download is running. We track CSV
  // and PDF separately so a user can fire both in parallel if desired.
  const [downloading, setDownloading] = useState(null); // "csv" | "pdf" | null
  // When dates change and the operator hasn't manually edited the subject/body,
  // refresh them with the new range. Once they type → we leave it alone.
  useEffect(() => {
    if (!touched) {
      setSubject(computedSubject);
      setBody(computedBody);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/email/events`, {
        params: { customer_id: customerId, kind, limit: 10 },
      });
      setHistory(data || []);
    } catch (_) { /* email history is optional */ }
  }, [customerId, kind]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Load this customer's historical transaction UNITS for the
  // per-Unit dropdown. P4-N2E (2026-02) — SOURCE CHANGE.
  // Previously fetched `/cards?customer_id=X&network=Y` (current
  // ownership) which silently dropped transferred cards' historical
  // units (e.g. JSONS Esso NR1001/NR1002 after transfer to Night
  // Road). Now reads from `/admin/units` — customer-scoped Mongo
  // distinct over `transaction.unit` — so historical units survive
  // card transfers. Card-management surfaces (Customer → Cards tab)
  // still read `/cards` for current ownership; unchanged.
  useEffect(() => {
    if (isPrice) return;
    let abort = false;
    (async () => {
      try {
        const params = { customer_id: customerId };
        if (wireNetworkCountry.network) params.network = wireNetworkCountry.network;
        if (wireNetworkCountry.country) params.country = wireNetworkCountry.country;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo)   params.date_to   = dateTo;
        const { data } = await api.get("/admin/units", { params });
        if (!abort) {
          const units = Array.isArray(data?.units) ? data.units : [];
          // Shape kept compatible with the render loop below —
          // `card_number` stores the unit string so the current
          // <option value>/<option key> continue to work; the
          // downstream `download()` maps this into a `unit=` query
          // param instead of the legacy `card_number=` param.
          const list = units.map((u) => ({ unit: u, card_number: u }));
          list.sort((a, b) => a.unit.localeCompare(b.unit, undefined, { numeric: true }));
          setCardOptions(list);
          if (cardId && !list.find((c) => c.card_number === cardId)) {
            setCardId("");
          }
        }
      } catch (_) { /* endpoint is optional — leave dropdown empty */ }
    })();
    return () => { abort = true; };
  }, [isPrice, customerId, network, wireNetworkCountry.network, wireNetworkCountry.country, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (noEmail) return;
    setBusy(true);
    const path = isPrice
      ? `/customers/${customerId}/email-price-list`
      : `/customers/${customerId}/email-transaction-report`;
    try {
      const { data } = await api.post(path, {
        date_from: dateFrom,
        date_to: dateTo,
        // Only ship the network filter for the tx-report flavour; the
        // price-list endpoint doesn't take a single-network override
        // (it has its own per-network column config). Feb 2026: also
        // ship the country for the PP-CA/PP-US lanes via the decomposed
        // wire pair — the email PDF builder now honours both.
        ...(isPrice ? {} : {
          network: wireNetworkCountry.network,
          country: wireNetworkCountry.country,
        }),
        subject_override: isPrice && !isEditedPriceListSubject(subject, customer.company_name) ? null : subject || null,
        body_override: isPrice && body === computedBody ? null : body || null,
        cc_operator: cc,
      });
      toast.success(`${isPrice ? "Price list" : "Transaction report"} emailed → ${data.to}`);
      if (data.excluded_older_stations?.length) {
        const ex = data.excluded_older_stations;
        const total = data.excluded_older_station_count ?? ex.length;
        toast.warning(`${total} station price(s) older than the report date were left out: `
          + ex.slice(0, 6).map((x) => `${x.station_id} ${x.station_name || ""} (${x.effective_date})`).join(", ")
          + (total > 6 ? ` +${total - 6} more` : ""), { duration: 15000 });
      }
      setSent(data);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  // Preview / verify the EXACT same report the customer will receive — same
  // endpoint, same `for_customer=true` projection, so what the admin sees on
  // disk is byte-identical to what gets attached to the email. Lets the
  // operator sanity-check pricing before hitting Send.
  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      const path = isPrice
        ? `/customers/${customerId}/price-report/${fmt}`
        : `/transactions/report/${fmt}`;
      const params = isPrice
        ? { date_from: dateFrom, date_to: dateTo }
        : {
            date_from: dateFrom,
            date_to: dateTo,
            customer_id: customerId,
            for_customer: true,
            // Feb 2026 PP-CA/US split: send the decomposed wire pair so
            // Petro-Pass CAD/L and Petro-Pass USD/GAL each hit the report
            // builder with the correct country filter (and, for PP-US,
            // trigger the derived State column + USD/GAL currency label).
            ...(wireNetworkCountry.network ? { network: wireNetworkCountry.network } : {}),
            ...(wireNetworkCountry.country ? { country: wireNetworkCountry.country } : {}),
            // Per-Unit / Per-Card filter — P4-N2E: send `unit=` (the
            // authoritative historical value stamped on the
            // transaction row) instead of `card_number=` so
            // transferred-card historical rows remain selectable
            // under the historical customer.
            ...(cardId ? { unit: cardId } : {}),
          };
      const filename = await downloadFromBackend(path, params);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to download ${fmt.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  // Bulk per-unit ZIP — one CSV + one PDF per fuel card that had activity
  // in the window, bundled into a single archive. Used by the operator
  // when each driver/unit needs their own sheet for a period.
  const downloadZip = async () => {
    setDownloading("zip");
    try {
      const params = {
        date_from: dateFrom,
        date_to: dateTo,
        customer_id: customerId,
        for_customer: true,
        // Feb 2026 PP-CA/US split: same decomposed wire pair as the CSV/
        // PDF download so the per-unit ZIP inherits the corrected lane.
        ...(wireNetworkCountry.network ? { network: wireNetworkCountry.network } : {}),
        ...(wireNetworkCountry.country ? { country: wireNetworkCountry.country } : {}),
      };
      const filename = await downloadFromBackend("/transactions/report/zip", params);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to build per-unit zip");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white w-full max-w-2xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="gf-card-header"><h3>{heading}</h3></div>

        <div className="gf-card-body space-y-3 text-sm overflow-y-auto">
          {noEmail ? (
            <div className="p-3 border border-amber-300 bg-amber-50 text-amber-900 text-xs" data-testid="quick-email-no-recipient">
              <Warning size={14} className="inline -mt-0.5 mr-1" />
              <b>{customer.company_name}</b> has no billing email on file. Add one in <i>Edit customer → Contact</i> first.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="overline">TO</span><div className="font-mono mt-1" data-testid="quick-email-to">{recipient}</div></div>
                <div><span className="overline">FROM</span><div className="font-mono mt-1">Get Fuel Card &lt;noreply@getfuelcard.com&gt;</div></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="overline mb-1">Date from</div>
                  <input type="date" className="gf-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="quick-email-date-from" />
                </div>
                <div>
                  <div className="overline mb-1">Date to</div>
                  <input type="date" className="gf-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="quick-email-date-to" />
                </div>
              </div>
              {!isPrice && (
                <div>
                  <div className="overline mb-1">Network</div>
                  <select
                    className="gf-input"
                    value={network}
                    onChange={(e) => {
                      // P4-N5B Slice C3 — Network / Country reconciliation
                      // delegated to the C1 shared adapter
                      // (`networkCountryCatalog.js`). Rules consumed:
                      //   All networks → All / CA / US
                      //   Esso         → All / CA / US
                      //   Pipeline     → CA only (locked)
                      //   Petro-Pass   → All / CA / US
                      const nextNet = e.target.value;
                      const validCountries = _quickEmailTxCountriesFor(nextNet);
                      const nextCountry = validCountries.includes(country)
                        ? country
                        : validCountries[0];
                      setNetwork(nextNet);
                      setCountry(nextCountry);
                    }}
                    data-testid="quick-email-network"
                    title="Filter the report to a specific provider. Country is picked separately below."
                  >
                    <option value="">All networks</option>
                    <option value="esso" data-testid="quick-email-network-opt-esso">Esso</option>
                    <option value="pipeline" data-testid="quick-email-network-opt-pipeline">Pipeline</option>
                    <option value="petro_pass" data-testid="quick-email-network-opt-petro-pass">Petro-Pass</option>
                  </select>
                </div>
              )}
              {!isPrice && (
                <div data-testid="quick-email-country-wrap">
                  <div className="overline mb-1">Country</div>
                  <select
                    className="gf-input"
                    value={country}
                    disabled={_quickEmailTxCountriesFor(network).length <= 1}
                    onChange={(e) => setCountry(e.target.value)}
                    data-testid="quick-email-country"
                    title="Geography scope. Pipeline is Canada-only and locks this selector."
                  >
                    {/* P4-N5B Slice C3 — Country options driven ENTIRELY
                        by the C1 capability matrix. A single-capability
                        lane (Pipeline = CA) renders exactly one option
                        and disables the selector so no impossible pair
                        can leave the modal. */}
                    {_quickEmailTxCountriesFor(network).map((c) => (
                      <option key={c || "all"} value={c}>{COUNTRY_LABELS[c]}</option>
                    ))}
                  </select>
                </div>
              )}
              {!isPrice && (
                <div data-testid="quick-email-card-wrap">
                  <div className="overline mb-1">Unit / Card</div>
                  <select
                    className="gf-input"
                    value={cardId}
                    onChange={(e) => setCardId(e.target.value)}
                    data-testid="quick-email-card"
                    title="Restrict the CSV/PDF to a single Unit / Card. Use ‘Download per-unit (zip)’ below to receive one file per Unit instead."
                  >
                    <option value="">All units / All cards</option>
                    {cardOptions.map((c) => {
                      // P4-N2E — options now represent units (label
                      // and value are both the unit string). The
                      // legacy `${unit} · ${last5}` label required a
                      // card_number and no longer applies since a
                      // historical unit may span multiple card
                      // rotations.
                      const unit = (c.unit || "").trim();
                      return (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      );
                    })}
                  </select>
                  {cardOptions.length === 0 && (
                    <div className="text-[11px] text-zinc-500 mt-1">
                      No fuel cards on file for this customer / network.
                    </div>
                  )}
                </div>
              )}
              <div className="text-[11px] text-zinc-500">
                {isPrice ? "Defaults to today's prices." : "Defaults to yesterday's transactions."} Pick a wider range for a historical sheet.
              </div>

              <div>
                <div className="overline mb-1">Subject</div>
                <input className="gf-input" value={subject} onChange={(e) => { setSubject(e.target.value); setTouched(true); }} data-testid="quick-email-subject"/>
              </div>
              <div>
                <div className="overline mb-1">Message</div>
                <textarea className="gf-input" rows={8} value={body} onChange={(e) => { setBody(e.target.value); setTouched(true); }} data-testid="quick-email-body"/>
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={cc} onChange={(e) => setCc(e.target.checked)} data-testid="quick-email-cc"/>
                CC <span className="font-mono">hello@getfuelcard.com</span>
                <span className="text-zinc-500">— keeps a copy in your own inbox</span>
              </label>
            </>
          )}

          {sent && (
            <div className="p-2 border border-green-300 bg-green-50 text-green-900 text-xs" data-testid="quick-email-sent-banner">
              <CheckCircle size={14} className="inline -mt-0.5 mr-1"/>
              Sent. Mailgun id <span className="font-mono">{(sent.message_id || "").slice(0, 28)}…</span>
              {sent.cc?.length > 0 && <> · CC {sent.cc.join(", ")}</>}
              <div className="text-[10px] text-green-800/80 mt-0.5">Delivery / open status will appear in the history below as Mailgun fires the webhook.</div>
            </div>
          )}

          {history.length > 0 && (
            <div className="border-t border-zinc-200 pt-3">
              <div className="overline mb-2">Send history (last 10)</div>
              <ul className="text-xs space-y-1 font-mono" data-testid="quick-email-history">
                {history.map((h) => (
                  <li key={h.event_id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 py-1 border-b border-zinc-100" data-testid={`email-history-row-${h.event_id}`}>
                    <StatusPill status={h.status}/>
                    <span className="text-zinc-500 whitespace-nowrap" title={`UTC: ${h.sent_at}`}>{formatTorontoTimestamp(h.sent_at)} ET</span>
                    {h.source && <span className={`px-1 rounded border text-[10px] ${h.source === "bulk" ? "border-sky-300 text-sky-700" : "border-zinc-300 text-zinc-600"}`} data-testid={`email-history-source-${h.event_id}`}>{h.source.toUpperCase()}</span>}
                    {h.report_date_from && <span className="text-zinc-500 whitespace-nowrap">for {h.report_date_from}{h.report_date_to && h.report_date_to !== h.report_date_from ? ` → ${h.report_date_to}` : ""}</span>}
                    {h.opened_at && <Eye size={11} className="text-blue-600"/>}
                    {h.clicked_at && <Cursor size={11} className="text-blue-700"/>}
                    <div className="basis-full flex flex-wrap gap-x-2 pl-1 text-[11px]">
                      <span className="text-zinc-800 break-all" data-testid={`email-history-to-${h.event_id}`}>{h.to?.[0]}</span>
                      {h.campaign_id && <span className="text-zinc-400" data-testid={`email-history-campaign-${h.event_id}`}>{h.campaign_id}</span>}
                      {h.resolution && <span className="text-zinc-600" data-testid={`email-history-resolution-${h.event_id}`} title={`${h.resolution.at}${h.resolution.provider_reference ? ` · ${h.resolution.provider_reference}` : ""}`}>{h.resolution.action === "confirm_sent" ? "Admin confirmed Mailgun accepted" : "Admin confirmed not accepted"} by {h.resolution.by}</span>}
                      {h.error && <span className={h.resolution ? "text-zinc-400 line-through" : "text-red-700"} title={h.error}>{h.error}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
          <button className="gf-btn gf-btn-outline" onClick={onClose} disabled={busy} data-testid="quick-email-close">{sent ? "Close" : "Cancel"}</button>
          {!noEmail && (
            <>
              {/*
                Download buttons sit BETWEEN Cancel and Send by design — the
                operator's mental flow is: review dates → preview the report
                (CSV / PDF) → send. Putting download adjacent to Send keeps
                the eye-path moving left-to-right without back-tracking.
                These hit the SAME endpoints the email uses for the
                attachment, so what you download = what the customer receives.

                P4-N5C UX Correction R3 (2026-09-19): the price-list flavour
                of this modal is the "Email Price Report" workflow — an
                email-delivery-only surface per operator directive. The
                Download CSV / Download PDF controls are therefore hidden
                for `isPrice`; they remain intact for the transaction-report
                flavour, which is unrelated to N5C.
              */}
              {!isPrice && (
                <>
                  <button
                    className="gf-btn gf-btn-outline"
                    onClick={() => download("csv")}
                    disabled={busy || downloading !== null}
                    data-testid="quick-email-download-csv"
                    title="Download the report as a spreadsheet (matches the email attachment exactly)"
                  >
                    <DownloadSimple size={14}/> {downloading === "csv" ? "Preparing…" : "Download CSV"}
                  </button>
                  <button
                    className="gf-btn gf-btn-outline"
                    onClick={() => download("pdf")}
                    disabled={busy || downloading !== null}
                    data-testid="quick-email-download-pdf"
                    title="Download the report as a PDF (matches the email attachment exactly)"
                  >
                    <DownloadSimple size={14}/> {downloading === "pdf" ? "Preparing…" : "Download PDF"}
                  </button>
                </>
              )}
              {!isPrice && (
                <button
                  className="gf-btn gf-btn-outline"
                  onClick={downloadZip}
                  disabled={busy || downloading !== null}
                  data-testid="quick-email-download-zip"
                  title="Bundle every Unit / Card with activity in this window as separate CSV+PDF files inside a single zip."
                >
                  <DownloadSimple size={14}/> {downloading === "zip" ? "Preparing…" : "Download per-unit (zip)"}
                </button>
              )}
            </>
          )}
          {!sent && !noEmail && (
            <button className="gf-btn gf-btn-primary" onClick={send} disabled={busy || !subject.trim() || downloading !== null} data-testid="quick-email-send">
              <EnvelopeSimple size={14}/> {busy ? "Sending…" : "Send"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    sent: "bg-zinc-100 text-zinc-700 border-zinc-300",
    delivered: "bg-green-100 text-green-800 border-green-300",
    opened: "bg-blue-100 text-blue-800 border-blue-300",
    clicked: "bg-blue-100 text-blue-900 border-blue-400",
    bounced: "bg-red-100 text-red-800 border-red-300",
    complained: "bg-red-100 text-red-900 border-red-400",
    failed: "bg-red-100 text-red-800 border-red-300",
    mock_sent: "bg-amber-50 text-amber-800 border-amber-300",
    sending: "bg-sky-50 text-sky-800 border-sky-300",
    skipped: "bg-zinc-50 text-zinc-600 border-zinc-300",
    needs_review: "bg-orange-100 text-orange-900 border-orange-400",
    accepted_admin_confirmed: "bg-zinc-100 text-zinc-700 border-zinc-400",
    not_accepted_admin_confirmed: "bg-red-50 text-red-800 border-red-300",
  };
  const label = (status || "—").toUpperCase();
  return (
    <span className={`px-1.5 py-0 text-[10px] tracking-wider border ${styles[status] || styles.sent}`} style={{ fontFamily: "inherit" }}>
      {label}
    </span>
  );
}

// Shared Price Report defaults (R1.2). Unedited text is not sent as an override,
// so the server stamps the Toronto date and per-network dates at submission.
export const priceListDefaultSubject = (company) =>
  `Get Fuel Card Price List - ${company} - ${new Intl.DateTimeFormat("en-CA", { timeZone: "America/Toronto" }).format(new Date())}`;

export const priceListDefaultBody = (company) =>
  `Hello,\n\nPlease find attached the latest fuel price list for ${company}, effective on the dates listed below.\n\n` +
  "If anything looks off or you'd like to add/remove a region, please contact your account manager.\n\n" +
  "Effective dates in this email:\n- (each network's actual effective date is added automatically when sent)\n\n— Get Fuel Card";

export const isEditedPriceListSubject = (subject, company) =>
  !/^Get Fuel Card Price List - .+ - \d{4}-\d{2}-\d{2}$/.test(subject || "") ||
  !(subject || "").startsWith(`Get Fuel Card Price List - ${company} - `);
